import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  GetBookingsDataTableQuery,
  ProjectionKind,
  useGetBookingsDataTable,
} from 'generated/resources/datatable.graphql'
import { uniq } from 'lodash'

import { observer } from 'mobx-react'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { LocationNodes } from 'modules/locations/hooks/useLocations'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { BookingNode } from 'modules/space_control/hooks/bookings/useBooking'

import BookingsActionButtons from './BookingsPopoverMenu'
import EmployeeField from './EmployeeField'
import assigneeFilter from './assigneeFilter'
import dateFilter from './dateFilter'
import locationFilter from './locationFilter'
import spaceTypeFilter from './spaceTypeFilter'

interface Props {
  locations: LocationNodes
}

function Table() {
  const { data } = useDataTable<GetBookingsDataTableQuery>()

  useUnsafeAsyncData(async () => {
    const updateVerb = 'spacecontrol.booking.upsert'
    const cancelVerb = 'spacecontrol.booking.bulk_cancel'
    const employeeIds: number[] = data?.map((booking) => booking?.access?.employee?.id) ?? []

    if (employeeIds.length < 1) return Promise.resolve()
    const endpoints = []
    if (employeeIds.length > 0 && !users.current.policy.can('spaceControl.bookingEditEverybody'))
      endpoints.push(
        policyAuthorizations.authorizePolicies(
          updateVerb,
          uniq(employeeIds).map((id) => ({
            id: id,
            policyTargetClass: '::Employee',
          }))
        )
      )
    if (employeeIds.length > 0 && !users.current.policy.can('spaceControl.bookingCancelEverybody'))
      policyAuthorizations.authorizePolicies(
        cancelVerb,
        uniq(employeeIds).map((id) => ({
          id: id,
          policyTargetClass: '::Employee',
        }))
      )

    return Promise.all(endpoints)
  }, [data])

  return <DataTable.Table />
}

function BookingsDatatable({ locations }: Props) {
  const source = useGetBookingsDataTable([
    {
      identifier: 'date',
      title: i18n.t('space_control.my-bookings.datatable.headers.date_time'),
      kind: ProjectionKind.Custom,
      sortBy: 'date',
      render: ({ date, startsAt, endsAt }) => (
        <Box>
          <Text.Normal weight="medium">
            {startsAt} - {endsAt}
          </Text.Normal>
          <Text.Normal>{date && dateBackendToUI(date)}</Text.Normal>
        </Box>
      ),
      template: '{{ date }} {{startsAt}} {{endsAt}}',
    },
    {
      identifier: 'location',
      title: i18n.t('space_control.my-bookings.datatable.headers.location'),
      kind: ProjectionKind.Custom,
      template:
        '{{ workSpace_workArea_location_name }}, {{workSpace_workArea_name}}, {{workSpace_name}}',
      render: ({ workSpace_workArea_location_name, workSpace_workArea_name, workSpace_name }) => (
        <Box>
          <Text.Normal weight="medium">{workSpace_name}</Text.Normal>
          <Text.Normal>{`${workSpace_workArea_location_name}, ${workSpace_workArea_name}`}</Text.Normal>
        </Box>
      ),
    },
    {
      identifier: 'booked',
      title: i18n.t('space_control.my-bookings.datatable.headers.assignee'),
      kind: ProjectionKind.Custom,
      render: ({ access_fullName, access_avatar }) => (
        <EmployeeField fullName={access_fullName} avatar={access_avatar} />
      ),
      template: '{{ access_fullName }}',
    },
    {
      identifier: 'type',
      title: i18n.t('space_control.my-bookings.datatable.headers.type'),
      kind: ProjectionKind.Custom,
      render: ({ workSpace_workSpaceTypeName }) => (
        <Box inline>
          <Tag type="grey">{workSpace_workSpaceTypeName}</Tag>
        </Box>
      ),
      template: '{{ workSpace_workSpaceTypeName }}',
    },
    {
      identifier: 'actions',
      kind: ProjectionKind.Decorative,
      render: (booking) => <BookingsActionButtons booking={booking as BookingNode} />,
      width: 120,
    },
  ])

  return (
    <Box padding="s16" flexDirection="row" alignItems="flexStart" gap="s16">
      <DataTable.Provider
        filters={[
          dateFilter(source),
          spaceTypeFilter(source),
          locationFilter(source, locations),
          assigneeFilter(source),
        ]}
        sort={{
          path: 'date',
        }}
        source={source}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            exportFileName="bookings"
            onExportCallback={() =>
              track(trackingEvents.spaceControl.bookings.requestExportBooking)
            }
          />
          <Table />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}

export default observer(BookingsDatatable)
